// Geometry helpers for L-shape playouts.
//
// A SuperSource box scales its source uniformly, so its normalized width and
// height both equal `size`. That is what makes the L self-consistent: when the
// program window is snapped flush into a corner, the sidebar's width fraction
// and the banner's height fraction are both `1 - size`, which is exactly the
// constraint a non-16:9 notch would otherwise force you to solve with
// letterboxing or crop.
//
// Geometry is stored as center + size rather than a corner enum so the operator
// can nudge off the snap to match artwork that isn't mathematically flush.
//
// What the switcher draws is not exactly that ideal rectangle: edges snap to
// even pixels and the box has a 1.5 px soft edge. The pixel maths lives in
// supersource-geometry.ts; the helpers below go through it.
import { animationSeconds } from "./animation-duration.js";
import { SS_FRAME_HEIGHT, SS_FRAME_WIDTH, ssBoxGeometry, ssClipToFrame, ssFitBox, } from "./supersource-geometry.js";
/** Default magnet radius, in normalized units. */
export const SNAP_TOLERANCE = 0.01;
/**
 * Center position that places a window of `size` flush into a corner.
 *
 * Flush means the box's outer edges sit exactly on the frame edges: for
 * top-left, `x - size/2 === 0` and `y - size/2 === 0`.
 */
export function snapPosition(target, size) {
    const half = size / 2;
    switch (target) {
        case "top-left":
            return { x: half, y: half };
        case "top-right":
            return { x: 1 - half, y: half };
        case "bottom-left":
            return { x: half, y: 1 - half };
        case "bottom-right":
            return { x: 1 - half, y: 1 - half };
    }
}
/**
 * Which corner a geometry is snapped to, or null when it is not flush against
 * two edges. Used by the editor to warn that the result isn't an L.
 */
export function detectSnapTarget(geometry, tolerance = SNAP_TOLERANCE) {
    const targets = [
        "top-left",
        "top-right",
        "bottom-left",
        "bottom-right",
    ];
    for (const target of targets) {
        const { x, y } = snapPosition(target, geometry.size);
        if (Math.abs(x - geometry.x) <= tolerance &&
            Math.abs(y - geometry.y) <= tolerance) {
            return target;
        }
    }
    return null;
}
/** True when the window forms a valid L (flush against two frame edges). */
export function isFlush(geometry, tolerance = SNAP_TOLERANCE) {
    return detectSnapTarget(geometry, tolerance) !== null;
}
/**
 * SuperSource box for a playout window. Squeezes never crop (the crop would
 * jump between frames), so the window is the whole 16:9 picture. Frame-aligned
 * edges are pushed past the frame so the box's soft edge never shows there.
 * See {@link ssFitBox}.
 */
export function playoutGeometryToSsBox(geometry) {
    return ssFitBox({
        x: geometry.x,
        y: geometry.y,
        width: geometry.size,
        height: geometry.size,
    }, { allowCrop: false });
}
/** Where the switcher actually draws the playout window, in 1080p pixels. */
export function playoutWindowPixels(geometry) {
    return ssBoxGeometry(playoutGeometryToSsBox(geometry)).window;
}
/**
 * Thickness of the L's arms in pixels, so the artwork can be drawn to match.
 * Only meaningful for a flush window.
 *
 * Measured from the window the switcher really draws (even pixels, size
 * quantised, edges pushed off-frame on the snapped sides), not from
 * `1 - size`. The window's own 1.5 px soft edge sits inside these numbers, so
 * artwork drawn to them is overlapped by the blend, never short of it.
 */
export function barThickness(size, frameWidth, frameHeight, target = "top-left") {
    const rect = playoutWindowPixels({ ...snapPosition(target, size), size });
    const left = target === "top-left" || target === "bottom-left";
    const top = target === "top-left" || target === "top-right";
    const sidebar = left ? SS_FRAME_WIDTH - rect.right : rect.left;
    const banner = top ? SS_FRAME_HEIGHT - rect.bottom : rect.top;
    return {
        sidebar: Math.max(0, Math.round((sidebar * frameWidth) / SS_FRAME_WIDTH)),
        banner: Math.max(0, Math.round((banner * frameHeight) / SS_FRAME_HEIGHT)),
    };
}
/**
 * The rectangle the program window occupies on screen, normalized and
 * clipped to the frame. Uses the switcher's real geometry (see
 * {@link playoutWindowPixels}), so a preview drawn from it is pixel exact.
 */
export function windowRect(geometry) {
    const rect = ssClipToFrame(playoutWindowPixels(geometry)) ?? {
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
    };
    return {
        left: rect.left / SS_FRAME_WIDTH,
        top: rect.top / SS_FRAME_HEIGHT,
        width: (rect.right - rect.left) / SS_FRAME_WIDTH,
        height: (rect.bottom - rect.top) / SS_FRAME_HEIGHT,
    };
}
/**
 * The ideal (unquantised) rectangle of a geometry, normalized. Useful for
 * interaction maths such as drag handles; render with {@link windowRect}.
 */
export function idealWindowRect(geometry) {
    return {
        left: geometry.x - geometry.size / 2,
        top: geometry.y - geometry.size / 2,
        width: geometry.size,
        height: geometry.size,
    };
}
/** Fullscreen state - where every squeeze starts and ends. */
export const FULLSCREEN_GEOMETRY = { x: 0.5, y: 0.5, size: 1 };
export const EASING = {
    linear: (t) => t,
    "ease-in": (t) => t * t,
    "ease-out": (t) => t * (2 - t),
    "ease-in-out": (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
};
/**
 * Interpolate the squeeze.
 *
 * Because the box's left edge is `x - size/2` - linear in both x and size -
 * lerping x, y and size together keeps a flush window pinned to its corner on
 * every intermediate frame. The picture shrinks *into* the corner rather than
 * drifting toward it, with no special-casing.
 */
export function interpolateGeometry(from, to, progress, easing = "ease-in-out") {
    const t = EASING[easing](Math.min(1, Math.max(0, progress)));
    return {
        x: from.x + (to.x - from.x) * t,
        y: from.y + (to.y - from.y) * t,
        size: from.size + (to.size - from.size) * t,
    };
}
/**
 * Total seconds an item occupies the output: squeeze in, hold, squeeze out.
 * `frameMs` is one video frame of the switcher's mode.
 * Used by the scheduler to decide whether a late item can still finish before
 * the next one is due.
 */
export function itemDurationSeconds(inDurationMs, holdSeconds, outDurationMs, frameMs) {
    // The squeezes run as whole video frames (see animationFrameCount), so
    // this is what they really take on a switcher with `frameMs` frames.
    return (animationSeconds(inDurationMs, frameMs) +
        holdSeconds +
        animationSeconds(outDurationMs, frameMs));
}
